import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import { ResetApiState, ResponseWrapper } from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { City, CityStateTypes } from "./City.util";

const name = "city";
const extraActions = createExtraActions();

const CityInitialState: CityStateTypes = {
  createCity: ResetApiState(null),
  resetCreateCity: ResetApiState(null),
  cities: ResetApiState([]),
  updateDropOffPickUp: ResetApiState(null),
  resetUpdateDropOffPickUp: ResetApiState(null),
  deleteDropOffPickUp: ResetApiState(null),
  resetDeleteDropOffPickUp: ResetApiState(null),

  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createCity = createAsyncThunk<ResponseWrapper<City>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateCity = createAction(`/reset-create-${name}`);

  const updateDropOffPickUp = createAsyncThunk<ResponseWrapper<City>, any>(
    `/pickup-dropoff-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .post(`/${name}/pickup-dropoff/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetUpdateDropOffPickUp = createAction(
    `/reset-pickup-dropoff-${name}`
  );

  const deleteDropOffPickUp = createAsyncThunk<ResponseWrapper<City>, any>(
    `/delete-dropoff-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`dropoff-pickup/${id}`)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDeleteDropOffPickUp = createAction(
    `/reset-delete-dropoff-${name}`
  );

  const getCities = createAsyncThunk<City[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const resetStates = createAction(`/reset-states`);

  return {
    createCity,
    resetCreateCity,
    updateDropOffPickUp,
    resetUpdateDropOffPickUp,
    getCities,
    deleteDropOffPickUp,
    resetDeleteDropOffPickUp,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(builder: ActionReducerMapBuilder<CityStateTypes>) {
  return {
    ...createCityReducer(),
    ...resetCreateCity(),
    ...updateDropOffPickUp(),
    ...resetUpdateDropOffPickUp(),
    ...getCities(),
    ...deleteDropOffPickUp(),
    ...resetDeleteDropOffPickUp(),
    ...resetStatesReducer(),
  };

  function createCityReducer() {
    return {
      ...builder.addCase(extraActions.createCity.pending, (state, action) => {
        state.createCity = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.createCity.fulfilled, (state, action) => {
        state.createCity = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.createCity.rejected, (state, action) => {
        state.createCity = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetCreateCity() {
    return {
      ...builder.addCase(extraActions.resetCreateCity, (state, action) => {
        state.resetCreateCity = ResetApiState(true);
        state.createCity = ResetApiState(null);
      }),
    };
  }

  function updateDropOffPickUp() {
    return {
      ...builder.addCase(
        extraActions.updateDropOffPickUp.pending,
        (state, action) => {
          state.updateDropOffPickUp = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateDropOffPickUp.fulfilled,
        (state, action) => {
          state.updateDropOffPickUp = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateDropOffPickUp.rejected,
        (state, action) => {
          state.updateDropOffPickUp = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetUpdateDropOffPickUp() {
    return {
      ...builder.addCase(
        extraActions.resetUpdateDropOffPickUp,
        (state, action) => {
          state.resetUpdateDropOffPickUp = ResetApiState(true);
          state.updateDropOffPickUp = ResetApiState(null);
        }
      ),
    };
  }

  function getCities() {
    return {
      ...builder.addCase(extraActions.getCities.pending, (state, action) => {
        state.cities = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getCities.fulfilled, (state, action) => {
        state.cities = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getCities.rejected, (state, action) => {
        state.cities = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function deleteDropOffPickUp() {
    return {
      ...builder.addCase(
        extraActions.deleteDropOffPickUp.pending,
        (state, action) => {
          state.deleteDropOffPickUp = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteDropOffPickUp.fulfilled,
        (state, action) => {
          state.deleteDropOffPickUp = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteDropOffPickUp.rejected,
        (state, action) => {
          state.deleteDropOffPickUp = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteDropOffPickUp() {
    return {
      ...builder.addCase(
        extraActions.resetDeleteDropOffPickUp,
        (state, action) => {
          state.resetDeleteDropOffPickUp = ResetApiState(true);
          state.deleteDropOffPickUp = ResetApiState(null);
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createCity = ResetApiState(null);
        state.resetCreateCity = ResetApiState(null);
        state.updateDropOffPickUp = ResetApiState(null);
        state.resetUpdateDropOffPickUp = ResetApiState(null);
        state.cities = ResetApiState(null);
      }),
    };
  }
}

const CitySlice = createSlice({
  name,
  initialState: CityInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const CityActions = { ...CitySlice.actions, ...extraActions };
export const CityReducer = CitySlice.reducer;
